
export enum GroupAccessRights {
    /// The right to remote control devices.
    RemoteControl = 0x000001,

    /// The right to view the device file system during remote control.
    RemoteControlViewFiles = 0x000002,

    /// The right to update the device file system during remote control.
    RemoteControlUpdateFiles = 0x000004,

    /// The right to view the device registry during remote control.
    RemoteControlViewRegistry = 0x000008,

    /// The right to update the device registry during remote control.
    RemoteControlUpdateRegistry = 0x000010,

    /// The right to view the task manager tool during remote control.
    RemoteControlViewTasks = 0x000020,

    /// The right to terminate tasks using the task manage tool during remote control.
    RemoteControlManageTasks = 0x000040,

    /// The right to view device system info during remote control.
    RemoteControlViewSystemInfo = 0x000080,

    /// The right to send DOS commands during remote control.
    RemoteControlCommands = 0x000100,

    /// The right to run device-side scripts during remote control.
    RemoteControlScripting = 0x000200,

    /// The right to send keyboard and mouse input to the device during remote control.
    RemoteControlInput = 0x000400,

    /// The right to remote control devices without notifying the device user.
    RemoteControlWithoutNotification = 0x000800,

    /** The right to connect through ssl tunnel service to the agent host during remote control. */
    RemoteControlWebConsole = 0x001000,

    /// The right to view the contents of a device group.
    ViewGroup = 0x00100000000,

    /// The right to add, delete, rename, and move device groups.
    ModifyGroup = 0x00200000000,

    /// The right to manage access control lists for device groups.
    ConfigureGroupSecurity = 0x00400000000,

    /// The right to edit Advanced Settings configuration (excluding security).
    ConfigureAdvancedSettings = 0x00800000000,

    /// The right to send a message to a device.
    SendDeviceMessage = 0x02000000000,

    /// The right to send a script to a device.
    SendDeviceScript = 0x04000000000,

    /// The right to manage devices (right click menu) and move devices from one group to another.
    ManageAndMoveDevices = 0x08000000000,

    /// The right to add, delete, and edit device notes.
    UpdateDeviceNotes = 0x10000000000,

    /// The right to locate, track, and view location history for a device.
    LocationServices = 0x20000000000,

    /// The right to upgrade device agents.
    UpgradeDeviceAgents = 0x40000000000,

    /// The right to upgrade network agents.
    UpgradeNetworkAgents = 0x80000000000,

    /// The right to reset device passwords and generate unlock codes.
    DevicePasswordControl = 0x100000000000,

    /// The right to send device actions (lock, unlock, wipe etc).
    SendDeviceAction = 0x200000000000,

    /// The right to modify device user information
    ModifyDeviceUser = 0x400000000000,

    /// The right to target device group
    TargetGroup = 0x800000000000,

    /// All available remote control access rights.
    RemoteControlRights = 0xFFF,

    /// All available access rights.
    All = 0xFFFF00000FFF
}

export interface IUserInfo {
    flags: number,
    userId: number,
    userName: string
}

export class SecurityContext {
    private _myId: number;
    private _permissions: number;
    private _organizer: number;
    private _controller: number;
    private _users: IUserInfo[] = [];

    /* istanbul ignore next */
    public isOrganizer = (userId?: number) => userId != undefined ? userId == this._organizer : this._myId == this._organizer;

    /* istanbul ignore next */
    public isController = (userId?: number) => userId != undefined ? userId == this._controller : this._myId == this._controller;

    /* istanbul ignore next */
    public get permissions() { return this._permissions; }

    /* istanbul ignore next */
    public get users() { return this._users; }

    // only the following permissions are currently being used. Add more members as needed:

    /** The right to send keyboard and mouse input to the device during remote control */
    public RemoteControlInput: boolean;

    /** The right to view the task manager tool during remote control  */
    public RemoteControlViewTasks: boolean;

    /** The right to view the device file system during remote control */
    public RemoteControlViewFiles: boolean;

    /** The right to view device system info during remote control */
    public RemoteControlViewSystemInfo: boolean;

    /** The right to send DOS commands during remote control */
    public RemoteControlCommands: boolean;

    /** The right to terminate tasks using the task manage tool during remote control */
    public RemoteControlManageTasks: boolean;

    /** The right to connect through ssl tunnel service to the agent host during remote control */
    public RemoteControlWebConsole: boolean;

    initialize = (permissions: number) => {
        for (var permission of Object.keys(GroupAccessRights).filter((v) => isNaN(Number(v)))) {
            this[permission] = (permissions & GroupAccessRights[permission]) === GroupAccessRights[permission];
        }
    }

    // #mcrc-btn-file-explorer and #mcrc-btn-device-info elements do not exist any longer, jQuery code removed
}
